import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, AlertTriangle, Clock, Loader2, BarChart2,
  Star, TrendingUp, TrendingDown, ChevronRight, X,
} from 'lucide-react';
import api from '../services/api';

const fmt = (sec) => {
  const n = Math.round(Number(sec));
  if (!n || isNaN(n)) return '—';
  return `${Math.floor(n / 60)}m ${String(n % 60).padStart(2, '0')}s`;
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  try {
    const s = String(ts).replace(' ', 'T');
    const d = new Date(s.endsWith('Z') || s.includes('+') ? s : s + 'Z');
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return '—'; }
};

// SVG bar chart — active scoring days
function DailyBarChart({ data }) {
  const counts = data.map(d => d.count);
  const max = Math.max(...counts, 1);
  const todayStr = new Date().toISOString().slice(0, 10);
  const chartH = 110;
  const n = data.length;
  const barW = n > 10 ? 18 : 26;
  const gap = n > 10 ? 6 : 10;
  const totalW = n * (barW + gap) - gap;
  const ox = Math.max((320 - totalW) / 2, 4);

  return (
    <svg viewBox="0 -20 320 195" className="w-full">
      {data.map((d, i) => {
        const barH = d.count > 0 ? Math.max((d.count / max) * chartH, 6) : 3;
        const x = ox + i * (barW + gap);
        const y = chartH - barH;
        const dt = new Date(d.date + 'T12:00:00');
        const dayLabel = dt.toLocaleDateString('en-US', { weekday: 'short' });
        const dateLabel = dt.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
        const isToday = d.date === todayStr;
        return (
          <g key={d.date}>
            <rect x={x} y={y} width={barW} height={barH} fill={isToday ? '#f59e0b' : d.count > 0 ? '#fcd34d' : '#f3f4f6'} rx="3" />
            {d.count > 0 && (
              <text x={x + barW / 2} y={y - 5} textAnchor="middle" fill="#92400e" fontSize="8" fontWeight="700">
                {d.count.toLocaleString()}
              </text>
            )}
            <text x={x + barW / 2} y={chartH + 15} textAnchor="middle" fill={isToday ? '#92400e' : '#6b7280'} fontSize="8" fontWeight={isToday ? '700' : '500'}>
              {dayLabel}
            </text>
            <text x={x + barW / 2} y={chartH + 26} textAnchor="middle" fill="#9ca3af" fontSize="7.5">
              {dateLabel}
            </text>
          </g>
        );
      })}
      <line x1={ox - 4} y1={chartH} x2={ox + totalW + 4} y2={chartH} stroke="#e5e7eb" strokeWidth="1" />
    </svg>
  );
}

function DailyModal({ stats, onClose }) {
  const allData = stats.dailyScores || [];
  const todayStr = new Date().toISOString().slice(0, 10);
  const yesterdayStr = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  const todayCount = allData.find(d => d.date === todayStr)?.count || 0;
  const yesterdayCount = allData.find(d => d.date === yesterdayStr)?.count || 0;
  const totalShown = allData.reduce((s, d) => s + d.count, 0);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-800">Scoring Activity</h2>
            <p className="text-[10px] text-gray-400">Last 7 days</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-3 mb-4">
          <div className="flex-1 bg-amber-50 rounded-xl p-3 text-center">
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Today</p>
            <p className="text-2xl font-bold text-amber-700 tabular-nums">{todayCount.toLocaleString()}</p>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Yesterday</p>
            <p className="text-2xl font-bold text-gray-700 tabular-nums">{yesterdayCount.toLocaleString()}</p>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Total</p>
            <p className="text-2xl font-bold text-gray-700 tabular-nums">{totalShown.toLocaleString()}</p>
          </div>
        </div>

        <DailyBarChart data={allData} />
        <p className="text-[10px] text-gray-400 text-center mt-1">Amber = today · gray = no activity that day</p>
      </div>
    </div>
  );
}

function ScoreDistModal({ stats, onClose }) {
  const dist = stats.scoreDistribution || {};
  const total = (dist.excellent || 0) + (dist.good || 0) + (dist.needsImprovement || 0) + (dist.critical || 0);

  const buckets = [
    { label: 'Excellent', range: '90 – 105', count: dist.excellent || 0, bar: 'bg-emerald-400', text: 'text-emerald-700', badge: 'bg-emerald-50 text-emerald-700' },
    { label: 'Good', range: '75 – 89', count: dist.good || 0, bar: 'bg-amber-400', text: 'text-amber-700', badge: 'bg-amber-50 text-amber-700' },
    { label: 'Needs Work', range: '60 – 74', count: dist.needsImprovement || 0, bar: 'bg-orange-400', text: 'text-orange-700', badge: 'bg-orange-50 text-orange-700' },
    { label: 'Critical', range: 'Below 60', count: dist.critical || 0, bar: 'bg-red-400', text: 'text-red-600', badge: 'bg-red-50 text-red-600' },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-gray-800">Score Distribution</h2>
            <p className="text-[10px] text-gray-400">{total.toLocaleString()} scored calls</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {buckets.map(b => {
            const pct = total > 0 ? Math.round((b.count / total) * 100) : 0;
            return (
              <div key={b.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${b.badge}`}>{b.label}</span>
                    <span className="text-[11px] text-gray-400">{b.range}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold tabular-nums ${b.text}`}>{b.count.toLocaleString()}</span>
                    <span className="text-[11px] text-gray-400 w-8 text-right tabular-nums">{pct}%</span>
                  </div>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${b.bar} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[10px] text-gray-400 text-center mt-5">Score out of 105 total points</p>
      </div>
    </div>
  );
}

function MonthlyModal({ stats, onClose }) {
  const data = stats.monthlyScores || [];
  const counts = data.map(d => d.count);
  const max = Math.max(...counts, 1);
  const chartH = 110, barW = 34, gap = 12;
  const totalW = data.length * (barW + gap) - gap;
  const ox = Math.max((320 - totalW) / 2, 4);
  const nowKey = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; })();

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-800">Monthly Scoring</h2>
            <p className="text-[10px] text-gray-400">Current month + previous 5 months</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <svg viewBox="0 -20 320 195" className="w-full">
          {data.map((d, i) => {
            const barH = d.count > 0 ? Math.max((d.count / max) * chartH, 6) : 3;
            const x = ox + i * (barW + gap);
            const y = chartH - barH;
            const isCurrent = d.month === nowKey;
            const [yr, mo] = d.month.split('-');
            const monthLabel = new Date(Number(yr), Number(mo) - 1, 1).toLocaleDateString('en-US', { month: 'short' });
            const yearLabel = yr;
            return (
              <g key={d.month}>
                <rect x={x} y={y} width={barW} height={barH} fill={isCurrent ? '#f59e0b' : d.count > 0 ? '#fcd34d' : '#f3f4f6'} rx="3" />
                {d.count > 0 && (
                  <text x={x + barW / 2} y={y - 5} textAnchor="middle" fill="#92400e" fontSize="8" fontWeight="700">
                    {d.count.toLocaleString()}
                  </text>
                )}
                <text x={x + barW / 2} y={chartH + 14} textAnchor="middle" fill={isCurrent ? '#92400e' : '#6b7280'} fontSize="9" fontWeight={isCurrent ? '700' : '500'}>
                  {monthLabel}
                </text>
                <text x={x + barW / 2} y={chartH + 25} textAnchor="middle" fill="#9ca3af" fontSize="7.5">
                  {yearLabel}
                </text>
              </g>
            );
          })}
          <line x1={ox - 4} y1={chartH} x2={ox + totalW + 4} y2={chartH} stroke="#e5e7eb" strokeWidth="1" />
        </svg>
        <p className="text-[10px] text-gray-400 text-center mt-1">Amber = current month · gray = no activity</p>
      </div>
    </div>
  );
}

// SVG donut pie chart
function DonutChart({ slices }) {
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  if (!total) return null;

  const cx = 100, cy = 100, r = 80, ir = 52;
  let angle = -Math.PI / 2;

  const paths = slices
    .filter(sl => sl.value > 0)
    .map(sl => {
      const sweep = (sl.value / total) * 2 * Math.PI;
      const sa = angle, ea = angle + sweep;
      angle = ea;
      const la = sweep > Math.PI ? 1 : 0;
      const cos = (a) => Math.cos(a), sin = (a) => Math.sin(a);
      const d = [
        `M ${cx + r * cos(sa)} ${cy + r * sin(sa)}`,
        `A ${r} ${r} 0 ${la} 1 ${cx + r * cos(ea)} ${cy + r * sin(ea)}`,
        `L ${cx + ir * cos(ea)} ${cy + ir * sin(ea)}`,
        `A ${ir} ${ir} 0 ${la} 0 ${cx + ir * cos(sa)} ${cy + ir * sin(sa)}`,
        'Z',
      ].join(' ');
      return { ...sl, d };
    });

  return (
    <svg viewBox="0 0 200 200" className="w-44 h-44 shrink-0">
      {paths.map((p, i) => <path key={i} d={p.d} fill={p.fill} />)}
      <text x="100" y="96" textAnchor="middle" fill="#1f2937" fontSize="13" fontWeight="700">
        {total.toLocaleString()}
      </text>
      <text x="100" y="112" textAnchor="middle" fill="#9ca3af" fontSize="9.5">
        Total Calls
      </text>
    </svg>
  );
}

function PieModal({ stats, onClose }) {
  const totalScored = Number(stats.totalScored) || 0;
  const totalAll = Number(stats.totalAll) || totalScored;
  const noTranscript = Number(stats.noTranscript) || 0;
  const pending = Number(stats.pending) || 0;
  const unscored = totalAll - totalScored;

  const pct = (v) => totalAll > 0 ? Math.round((v / totalAll) * 100) : 0;

  // When we have a real no-transcript count from DB, split into 3 slices.
  // Otherwise collapse all unscored into one "No Transcript" slice (known from diagnostics).
  const allSlices = noTranscript > 0 ? [
    { label: 'Scored', value: totalScored, fill: '#f59e0b', pct: pct(totalScored) },
    { label: 'No Transcript', value: noTranscript, fill: '#d1d5db', pct: pct(noTranscript) },
    { label: 'Pending Score', value: pending, fill: '#93c5fd', pct: pct(pending) },
  ] : [
    { label: 'Scored', value: totalScored, fill: '#f59e0b', pct: pct(totalScored) },
    { label: 'No Transcript', value: unscored, fill: '#d1d5db', pct: pct(unscored) },
  ];
  const slices = allSlices.filter(sl => sl.value > 0);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold text-gray-800">Call Coverage Breakdown</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-6">
          <DonutChart slices={slices} />

          <div className="flex flex-col gap-3 flex-1">
            {slices.map((sl) => (
              <div key={sl.label} className="flex items-center gap-2.5">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: sl.fill }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-700">{sl.label}</p>
                  <p className="text-sm font-bold text-gray-900 tabular-nums">
                    {Number(sl.value).toLocaleString()}
                    <span className="text-xs font-normal text-gray-400 ml-1">{sl.pct}%</span>
                  </p>
                </div>
              </div>
            ))}
            <div className="mt-1 pt-3 border-t border-gray-100">
              <p className="text-[10px] text-gray-400 leading-relaxed">
                "No Transcript" calls are pre-May 2026 records with no conversation identifier — they cannot be scored.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImprovedModal({ improved, onClose }) {
  const scores = improved.scores || [];
  const n = scores.length;
  if (n === 0) return null;

  const half = Math.floor(n / 2);
  const W = 320, H = 130, padL = 28, padR = 12, padT = 20, padB = 30;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const minScore = Math.max(0, Math.min(...scores.map(s => s.score)) - 10);
  const maxScore = Math.min(105, Math.max(...scores.map(s => s.score)) + 10);
  const range = maxScore - minScore || 1;

  const px = (i) => padL + (i / (n - 1)) * chartW;
  const py = (s) => padT + chartH - ((s - minScore) / range) * chartH;

  const olderPts = scores.slice(0, half).map((s, i) => [px(i), py(s.score)]);
  const recentPts = scores.slice(half).map((s, i) => [px(half + i), py(s.score)]);
  const allPts = scores.map((s, i) => [px(i), py(s.score)]);

  const polyline = (pts) => pts.map(([x, y]) => `${x},${y}`).join(' ');

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Most Improved</p>
            <h3 className="text-base font-bold text-gray-800">{improved.agentName}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex gap-4 mb-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-600">+{improved.delta}</p>
            <p className="text-[10px] text-gray-400">pts improved</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-amber-600">{improved.recentAvg}/105</p>
            <p className="text-[10px] text-gray-400">recent avg</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-700">{n}</p>
            <p className="text-[10px] text-gray-400">calls scored</p>
          </div>
        </div>

        <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
          {/* Y gridlines */}
          {[0, 0.25, 0.5, 0.75, 1].map((t) => {
            const y = padT + chartH - t * chartH;
            const label = Math.round(minScore + t * range);
            return (
              <g key={t}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#f3f4f6" strokeWidth="1" />
                <text x={padL - 4} y={y + 3} textAnchor="end" fill="#9ca3af" fontSize="7">{label}</text>
              </g>
            );
          })}
          {/* Divider line between older / recent */}
          {n > 1 && (
            <line x1={px(half - 0.5)} y1={padT} x2={px(half - 0.5)} y2={padT + chartH} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="3,3" />
          )}
          {/* Older half line — gray */}
          {olderPts.length > 1 && <polyline points={polyline(olderPts)} fill="none" stroke="#d1d5db" strokeWidth="2" strokeLinejoin="round" />}
          {/* Recent half line — emerald */}
          {recentPts.length > 1 && <polyline points={polyline(recentPts)} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinejoin="round" />}
          {/* Connect the two halves */}
          {olderPts.length > 0 && recentPts.length > 0 && (
            <line x1={olderPts[olderPts.length - 1][0]} y1={olderPts[olderPts.length - 1][1]} x2={recentPts[0][0]} y2={recentPts[0][1]} stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="3,3" />
          )}
          {/* Dots + score labels */}
          {allPts.map(([x, y], i) => {
            const isRecent = i >= half;
            return (
              <g key={i}>
                <circle cx={x} cy={y} r="3.5" fill={isRecent ? '#10b981' : '#9ca3af'} />
                <text x={x} y={y - 6} textAnchor="middle" fill={isRecent ? '#065f46' : '#6b7280'} fontSize="7" fontWeight="600">
                  {scores[i].score}
                </text>
              </g>
            );
          })}
          {/* X-axis labels — show first, halfway, last */}
          {[0, half, n - 1].filter((v, i, a) => a.indexOf(v) === i).map(i => (
            <text key={i} x={px(i)} y={H - 4} textAnchor="middle" fill="#9ca3af" fontSize="7">
              {scores[i].date ? scores[i].date.slice(5) : ''}
            </text>
          ))}
          <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke="#e5e7eb" strokeWidth="1" />
        </svg>

        <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-gray-300 inline-block" /> Older calls</span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-500 inline-block" /> Recent calls</span>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color = 'text-amber-600', onClick }) {
  return (
    <div
      className={`bg-white border border-gray-100 rounded-2xl p-5 shadow-sm ${onClick ? 'cursor-pointer hover:border-amber-300 hover:shadow-md transition-all' : ''}`}
      onClick={onClick}
    >
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-3xl font-bold tabular-nums ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      {onClick && <p className="text-[10px] text-amber-500 mt-1.5 font-medium">Click to see breakdown →</p>}
    </div>
  );
}

function AgentBar({ rank, agentName, avgScore, totalCalls, accent, onClick }) {
  const pct = Math.min((avgScore / 105) * 100, 100);
  return (
    <div>
      <div className="flex justify-between items-center text-xs mb-1">
        <span className="flex items-center gap-1.5">
          <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${accent.badge}`}>{rank}</span>
          <button onClick={onClick} className="text-gray-700 font-medium hover:text-amber-600 hover:underline transition-colors text-left">
            {agentName}
          </button>
        </span>
        <span className={`font-semibold tabular-nums ${accent.text}`}>{Math.round(avgScore)}/105</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${accent.bar}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[10px] text-gray-400 mt-0.5">{totalCalls} calls reviewed</p>
    </div>
  );
}

export default function QADashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPie, setShowPie] = useState(false);
  const [showDaily, setShowDaily] = useState(false);
  const [showMonthly, setShowMonthly] = useState(false);
  const [showDist, setShowDist] = useState(false);
  const [showImproved, setShowImproved] = useState(false);

  useEffect(() => {
    api.get('/reports/call-center/qa/statistics')
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">QA Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Quality assurance overview across all scored calls</p>
        </div>
        <button
          onClick={() => navigate('/qa-call-evaluation')}
          className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm"
        >
          <Phone className="w-4 h-4" />
          QA Call Evaluation
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-7 h-7 animate-spin text-amber-400" />
        </div>
      ) : !stats ? (
        <div className="bg-white border border-gray-100 rounded-2xl flex flex-col items-center justify-center py-20 text-gray-400 shadow-sm">
          <AlertTriangle className="w-8 h-8 mb-2 text-amber-300" />
          <p className="text-sm">Could not load statistics</p>
        </div>
      ) : (
        <>
          {/* Modals */}
          {showPie && <PieModal stats={stats} onClose={() => setShowPie(false)} />}
          {showDaily && <DailyModal stats={stats} onClose={() => setShowDaily(false)} />}
          {showMonthly && <MonthlyModal stats={stats} onClose={() => setShowMonthly(false)} />}
          {showDist && <ScoreDistModal stats={stats} onClose={() => setShowDist(false)} />}
          {showImproved && stats.mostImproved && <ImprovedModal improved={stats.mostImproved} onClose={() => setShowImproved(false)} />}

          {/* Row 1 — 4 KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              label="Total Calls Scored"
              value={Number(stats.totalScored).toLocaleString()}
              sub="calls evaluated"
            />
            <KpiCard
              label="Avg QA Score"
              value={stats.avgScore}
              sub="out of 105"
              onClick={() => setShowDist(true)}
            />
            <KpiCard
              label="Scored Today"
              value={Number(stats.scoredToday).toLocaleString()}
              sub="calls evaluated today"
              onClick={() => setShowDaily(true)}
            />
            <KpiCard
              label="Scored This Month"
              value={Number(stats.scoredThisMonth).toLocaleString()}
              sub="current calendar month"
              onClick={() => setShowMonthly(true)}
            />
          </div>

          {/* Row 2 — Scored This Hour + Handle Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                {stats.weekDelta == null ? (
                  <TrendingUp className="w-4 h-4 text-gray-400" />
                ) : stats.weekDelta >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500" />
                )}
                Score vs Last Week
              </h2>
              {stats.weekDelta == null ? (
                <p className="text-2xl font-bold text-gray-400">—</p>
              ) : (
                <p className={`text-3xl font-bold tabular-nums ${stats.weekDelta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {stats.weekDelta >= 0 ? '↑' : '↓'} {stats.weekDelta >= 0 ? '+' : ''}{stats.weekDelta} pts
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1.5">
                This week <span className="font-semibold text-gray-600">{stats.thisWeekAvg ?? '—'}</span>
                {' · '}Last week <span className="font-semibold text-gray-600">{stats.lastWeekAvg ?? '—'}</span>
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">Based on call_date, Mon → today</p>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-amber-400" />
                Avg Handle Time Breakdown
              </h2>
              {[
                { label: 'Talk Time', value: stats.avgTalkSec, color: 'bg-amber-400' },
                { label: 'Hold Time', value: stats.avgHoldSec, color: 'bg-amber-200' },
              ].map(({ label, value, color }) => {
                const pct = stats.avgHandleSec > 0 ? Math.min((value / stats.avgHandleSec) * 100, 100) : 0;
                return (
                  <div key={label} className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500 font-medium">{label}</span>
                      <span className="text-gray-700 font-semibold tabular-nums">{fmt(value)}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-gray-400 mt-1">Avg handle: <span className="font-semibold text-gray-600">{stats.avgHandleTime}</span></p>
            </div>
          </div>

          {/* Row 3 — Top 3 + Bottom 3 Agents */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-1.5">
                <Star className="w-4 h-4 text-amber-400" />
                Top 5 Agents
              </h2>
              <div className="space-y-3">
                {(stats.topAgents || []).map((a, i) => (
                  <AgentBar
                    key={a.agentName}
                    rank={i + 1}
                    agentName={a.agentName}
                    avgScore={a.avgScore}
                    totalCalls={a.totalCalls}
                    accent={{ badge: 'bg-amber-100 text-amber-700', text: 'text-amber-700', bar: 'bg-amber-400' }}
                    onClick={() => navigate(`/qa-call-evaluation?agent=${encodeURIComponent(a.agentName)}&status=evaluated`)}
                  />
                ))}
                {!(stats.topAgents || []).length && <p className="text-xs text-gray-400">Not enough data yet</p>}
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-1.5">
                <TrendingDown className="w-4 h-4 text-red-400" />
                Underperforming 5 Agents
              </h2>
              <div className="space-y-3">
                {(stats.bottomAgents || []).map((a, i) => (
                  <AgentBar
                    key={a.agentName}
                    rank={i + 1}
                    agentName={a.agentName}
                    avgScore={a.avgScore}
                    totalCalls={a.totalCalls}
                    accent={{ badge: 'bg-red-100 text-red-600', text: 'text-red-600', bar: 'bg-red-400' }}
                    onClick={() => navigate(`/qa-call-evaluation?agent=${encodeURIComponent(a.agentName)}&status=evaluated`)}
                  />
                ))}
                {!(stats.bottomAgents || []).length && <p className="text-xs text-gray-400">Not enough data yet</p>}
              </div>
            </div>
          </div>

          {/* Row 4 — Most Improved + Longest Calls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`bg-white border border-gray-100 rounded-2xl p-5 shadow-sm ${stats.mostImproved ? 'cursor-pointer hover:border-emerald-300 hover:shadow-md transition-all' : ''}`}
              onClick={() => stats.mostImproved && setShowImproved(true)}
            >
              <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Most Improved Agent
              </h2>
              {stats.mostImproved ? (
                <div className="flex flex-col gap-2">
                  <p className="text-lg font-bold text-gray-800">{stats.mostImproved.agentName}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-emerald-600">+{stats.mostImproved.delta}</span>
                    <span className="text-sm text-gray-500">pts improvement<br /><span className="text-gray-400 text-xs">recent avg vs older avg</span></span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Recent avg score: <span className="font-semibold text-gray-700">{stats.mostImproved.recentAvg}/105</span></p>
                  <p className="text-[10px] text-emerald-500 mt-1 font-medium">Click to see score trend →</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400">Not enough call history yet (need ≥ 4 calls per agent)</p>
              )}
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-1.5">
                <BarChart2 className="w-4 h-4 text-amber-400" />
                Longest Calls
              </h2>
              <div className="space-y-3">
                {(stats.longestCalls || []).map((c, i) => {
                  const maxSec = stats.longestCalls[0]?.handleTimeSec || 1;
                  const pct = (c.handleTimeSec / maxSec) * 100;
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-xs mb-1">
                        <button
                          onClick={() => {
                            if (c.vendorCallKey) {
                              navigate(`/qa-call-evaluation?callKey=${encodeURIComponent(c.vendorCallKey)}`);
                            } else {
                              navigate(`/qa-call-evaluation?agent=${encodeURIComponent(c.agentName || '')}&status=evaluated`);
                            }
                          }}
                          className="text-gray-600 font-medium hover:text-amber-600 hover:underline transition-colors text-left"
                        >
                          {c.agentName || '—'}
                          <span className="text-gray-400 font-normal"> · {c.callDate ? String(c.callDate).slice(0, 10) : '—'}</span>
                        </button>
                        <span className="font-semibold text-gray-700 tabular-nums">{c.handleTime}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-300 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      {c.qaScore != null && (
                        <p className="text-[10px] text-gray-400 mt-0.5">QA Score: {c.qaScore}/105</p>
                      )}
                    </div>
                  );
                })}
                {!(stats.longestCalls || []).length && <p className="text-xs text-gray-400">No data yet</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
